import type { InvoiceDiscountType, InvoiceLineItem, PoGstMode } from '../types';
import { NZ_GST_RATE } from './nzGst';
import { lineAmount } from './poUtils';

export interface InvoiceLevelDiscountInput {
  discountType?: InvoiceDiscountType | null;
  discountValue?: number | null;
}

export interface InvoiceLineTotalsBreakdown {
  lineId: string;
  gross: number;
  lineDiscount: number;
  /** Net after line discount and after proportional share of invoice-level discount. */
  lineNet: number;
  exGst: number;
  gst: number;
  incGst: number;
}

export interface InvoiceTotals {
  /** Sum of line gross (qty × price) before any discounts. */
  linesGross: number;
  lineDiscountTotal: number;
  /** Sum of line nets after per-line discounts, before invoice-level discount. */
  afterLineDiscounts: number;
  invoiceDiscountAmount: number;
  /** Amount after all discounts, in the invoice's gstMode currency basis. */
  afterAllDiscounts: number;
  subtotalExGst: number;
  gstAmount: number;
  totalIncGst: number;
  lineBreakdown: InvoiceLineTotalsBreakdown[];
}

function roundMoney(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

/** Apply a percentage or fixed discount to a non-negative base; never exceeds base. */
export function applyInvoiceDiscountAmount(
  base: number,
  discount?: InvoiceLevelDiscountInput | null,
): number {
  const safeBase = Math.max(0, Number(base) || 0);
  if (!discount?.discountType) return 0;
  const raw = Math.max(0, Number(discount.discountValue) || 0);
  if (raw <= 0 || safeBase <= 0) return 0;
  if (discount.discountType === 'percentage') {
    const pct = Math.min(100, raw);
    return roundMoney(safeBase * (pct / 100));
  }
  return roundMoney(Math.min(safeBase, raw));
}

function splitGst(amount: number, gstMode: PoGstMode): { exGst: number; gst: number; incGst: number } {
  if (gstMode === 'inclusive') {
    const exGst = amount * (100 / 115);
    const gst = amount * (15 / 115);
    return { exGst, gst, incGst: amount };
  }
  const exGst = amount;
  const gst = amount * NZ_GST_RATE;
  return { exGst, gst, incGst: exGst + gst };
}

function hasActiveDiscount(
  type?: InvoiceDiscountType | null,
  value?: number | null,
): boolean {
  return Boolean(type && Number(value) > 0);
}

/**
 * Invoice totals with optional per-line and invoice-level discounts.
 * Order: line discounts → sum → invoice-level discount → GST by gstMode.
 * When no discounts are set, results match computePoTotals for the same lines.
 */
export function computeInvoiceTotals(
  items: InvoiceLineItem[],
  gstMode: PoGstMode = 'inclusive',
  invoiceDiscount?: InvoiceLevelDiscountInput | null,
): InvoiceTotals {
  const anyLineDiscount = items.some((line) =>
    hasActiveDiscount(line.discountType, line.discountValue),
  );
  const anyInvoiceDiscount = hasActiveDiscount(
    invoiceDiscount?.discountType,
    invoiceDiscount?.discountValue,
  );

  // Fast path: identical math to computePoTotals (no rounding) when no discounts.
  if (!anyLineDiscount && !anyInvoiceDiscount) {
    const lineBreakdown: InvoiceLineTotalsBreakdown[] = items.map((line) => {
      const gross = lineAmount(line.quantity, line.unit, line.unitPrice, line.priceUnit);
      const { exGst, gst, incGst } = splitGst(gross, gstMode);
      return {
        lineId: line.lineId,
        gross,
        lineDiscount: 0,
        lineNet: gross,
        exGst,
        gst,
        incGst,
      };
    });
    const subtotalExGst = lineBreakdown.reduce((s, l) => s + l.exGst, 0);
    const gstAmount = lineBreakdown.reduce((s, l) => s + l.gst, 0);
    const totalIncGst = lineBreakdown.reduce((s, l) => s + l.incGst, 0);
    const linesGross = lineBreakdown.reduce((s, l) => s + l.gross, 0);
    return {
      linesGross,
      lineDiscountTotal: 0,
      afterLineDiscounts: linesGross,
      invoiceDiscountAmount: 0,
      afterAllDiscounts: linesGross,
      subtotalExGst,
      gstAmount,
      totalIncGst,
      lineBreakdown,
    };
  }

  const preInvoice = items.map((line) => {
    const gross = lineAmount(line.quantity, line.unit, line.unitPrice, line.priceUnit);
    const lineDiscount = applyInvoiceDiscountAmount(gross, {
      discountType: line.discountType,
      discountValue: line.discountValue,
    });
    const afterLine = Math.max(0, gross - lineDiscount);
    return { lineId: line.lineId, gross, lineDiscount, afterLine };
  });

  const linesGross = preInvoice.reduce((s, l) => s + l.gross, 0);
  const lineDiscountTotal = preInvoice.reduce((s, l) => s + l.lineDiscount, 0);
  const afterLineDiscounts = preInvoice.reduce((s, l) => s + l.afterLine, 0);
  const invoiceDiscountAmount = applyInvoiceDiscountAmount(afterLineDiscounts, invoiceDiscount);
  const afterAllDiscounts = Math.max(0, afterLineDiscounts - invoiceDiscountAmount);
  const scale = afterLineDiscounts > 0 ? afterAllDiscounts / afterLineDiscounts : 0;

  const lineBreakdown: InvoiceLineTotalsBreakdown[] = preInvoice.map((line) => {
    const lineNet = line.afterLine * scale;
    const { exGst, gst, incGst } = splitGst(lineNet, gstMode);
    return {
      lineId: line.lineId,
      gross: line.gross,
      lineDiscount: line.lineDiscount,
      lineNet,
      exGst,
      gst,
      incGst,
    };
  });

  const overall = splitGst(afterAllDiscounts, gstMode);

  return {
    linesGross,
    lineDiscountTotal,
    afterLineDiscounts,
    invoiceDiscountAmount,
    afterAllDiscounts,
    subtotalExGst: overall.exGst,
    gstAmount: overall.gst,
    totalIncGst: overall.incGst,
    lineBreakdown,
  };
}
